import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useRefreshUserOnReturn, useReportTimezone } from '@/entities/user';
import { LOGIN_REQUIRED_EVENT } from '@/shared/api';
import { ROUTES } from '@/shared/config';
import { trackPageView } from '@/shared/lib/metrika';
import { isTelegram } from '@/shared/lib/platform';
import { isLandingVisitor, markLandingVisitor, takeAskTelegram } from '@/shared/lib/webSession';
import { ErrorScreen, LoadingView } from '@/shared/ui';
import { HomePage } from '@/pages/home';
import { ProfilePage } from '@/pages/profile';
import {
  DocumentPage,
  DocumentsPage,
  ChatPage,
  DialogueReportPage,
  DictionariesPage,
  HistoryPage,
  IdiomAllPage,
  IdiomDetailPage,
  LearnPage,
  MemoryPage,
  MistakesPage,
  MyListsManagePage,
  OnboardingPage,
  PersonalizationGoalPage,
  PersonalizationInterestsPage,
  PersonalizationPage,
  preloadOnboarding,
  ReviewPage,
  RoleplayAllPage,
  RoleplayDetailPage,
  PaymentDetailsPage,
  PremiumPage,
  SettingsPage,
  StatisticsPage,
  SubscriptionPage,
  TutorSelectPage,
  LevelCheckPage,
  LoginPage,
} from './lazyPages';
import { DesktopShell } from './DesktopShell';
import { MainLayout } from './MainLayout';

/** Стенд аватара тянет three.js и существует только в dev. */
const AvatarLabPage = lazy(() =>
  import('@/pages/avatar-lab').then((module) => ({ default: module.AvatarLabPage })),
);

/** Стенд распознавания речи — только dev. */
const SttLabPage = lazy(() =>
  import('@/pages/stt-lab').then((module) => ({ default: module.SttLabPage })),
);

/**
 * Куда вести с корня, решает бэкенд: `CurrentUser.onboardingCompleted` из
 * `GET /me` — а не локальный флаг, который живёт только в этом браузере и
 * пропадает при чистке хранилища, смене устройства или сбое localStorage в
 * WebView Telegram (см. `shared/lib/storage`), из-за чего пройденный
 * онбординг запускался заново.
 */
/** Слаг статьи лендинга из `?talk=` — только буквы, цифры и дефис, как у слагов блога. */
function landingSlug(search: string): string | null {
  const slug = new URLSearchParams(search).get('talk');
  return slug && /^[a-z0-9-]{1,80}$/.test(slug) ? slug : null;
}

function RootRedirect() {
  const { t } = useTranslation('AppRouter');
  const { user, error, isLoading, reload } = useCurrentUser();
  // Корень — та точка, через которую проходит каждый холодный запуск
  // мини-аппа (Telegram открывает именно `/`), поэтому пояс сообщается
  // отсюда: перелетел человек или сменил устройство — узнаем на первом же
  // запуске. Эффект успевает отправить запрос до редиректа.
  useReportTimezone(user);

  if (isLoading) return <LoadingView label={t('loadingProfile')} />;
  if (error || !user) {
    return <ErrorScreen className="min-h-dvh" onRetry={reload} />;
  }

  // Микрофон со статьи лендинга (`/?start=…&talk=<слаг>`): сразу в разговор
  // с тьютором статьи и её репликой, мимо онбординга — человек пришёл
  // поговорить, а не настраиваться. Гость к этому моменту уже заведён
  // (`prepareSession`: `start` в адресе — визит из рекламы). Онбординг он
  // не проходит вовсе (`isLandingVisitor`).
  const talk = landingSlug(window.location.search);
  if (talk) {
    markLandingVisitor();
    return <Navigate to={`${ROUTES.chat}?landing=${talk}`} replace />;
  }

  // Со статьи — онбординга нет вовсе (`isLandingVisitor`).
  if (!user.onboardingCompleted && !isLandingVisitor()) {
    // Чанк онбординга начинает грузиться уже здесь, до перехода (P0-5).
    void preloadOnboarding();
    return <Navigate to={ROUTES.onboarding} replace />;
  }
  // Гость веб-версии — тоже на главную: ленивая регистрация (WEB.md), входит
  // он позже — когда кончились бесплатные реплики или перед оплатой.
  return <Navigate to={ROUTES.home} replace />;
}

/**
 * Любой запрос гостя веб-версии, упёршийся в 403 `login_required`
 * (`shared/api/http.ts`), ведёт на вход — один раз здесь, а не в каждом
 * экране со своим «Повторить». В мини-аппе гостей нет, событие не приходит.
 */
/**
 * Просмотры страниц для Метрики веб-версии (`shared/lib/metrika.ts`): адрес
 * меняет роутер, а не браузер. Первый счётчик шлёт сам при запуске — здесь
 * только переходы. В Telegram счётчика нет, вызов ничего не делает.
 */
function MetrikaPageViews() {
  const { pathname, search } = useLocation();
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const url = window.location.origin + pathname + search;
    if (previous.current !== null && previous.current !== url) trackPageView(url, previous.current);
    previous.current = url;
  }, [pathname, search]);
  return null;
}

/**
 * «Уже занимаешься в нашем Telegram-боте?» — сразу после первого входа через
 * Яндекс/VK (флаг ставит `completeProviderLogin`). Отдельным чанком: в
 * мини-аппе и у вошедших давно он не нужен.
 */
const TelegramAskSheet = lazy(() =>
  import('@/features/web-auth').then((module) => ({ default: module.TelegramAskSheet })),
);

function TelegramAsk() {
  const [open, setOpen] = useState(() => !isTelegram() && takeAskTelegram());
  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <TelegramAskSheet onClose={() => setOpen(false)} />
    </Suspense>
  );
}

function LoginRequiredRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const toLogin = () => void navigate(ROUTES.login, { replace: true });
    window.addEventListener(LOGIN_REQUIRED_EVENT, toLogin);
    return () => window.removeEventListener(LOGIN_REQUIRED_EVENT, toLogin);
  }, [navigate]);
  return null;
}

/**
 * BrowserRouter, а не HashRouter: hash в мини-аппе занят самим Telegram — он
 * передаёт туда launch params (`#tgWebAppData=...`).
 *
 * В стартовом чанке только Главная и Профиль, остальные страницы — в своих
 * чанках и докачиваются в простое после первого рендера главной (см.
 * `lazyPages.ts`). Если тап пришёл раньше, чем догрузился чанк, показывается
 * скелет этой страницы — у каждой свой `Suspense` внутри `lazyPage`, а не
 * «мёртвый» прежний экран. Общий `Suspense` здесь — только для стенда аватара.
 *
 * `useTransitions={false}`: по умолчанию роутер оборачивает переход в
 * `startTransition`, и React вместо фолбэка `Suspense` держит прежнюю страницу,
 * пока чанк не придёт, — тап выглядел не сработавшим, приходилось жать дважды.
 */
export function AppRouter() {
  // После оплаты вне приложения `/me` в кеше устарел — см. хук.
  useRefreshUserOnReturn();
  return (
    <BrowserRouter useTransitions={false}>
      <LoginRequiredRedirect />
      <MetrikaPageViews />
      <TelegramAsk />
      <Suspense fallback={null}>
        <Routes>
          <Route path={ROUTES.root} element={<RootRedirect />} />
          <Route path={ROUTES.onboarding} element={<OnboardingPage />} />
          <Route path={ROUTES.login} element={<LoginPage />} />
          {/* Экраны вошедшего — под десктопным каркасом: на компьютере в браузере
            сайдбар и колонка, на телефоне и в Telegram — просто страница
            (`DesktopShell`). Онбординг и вход — снаружи: гостю сайдбар с
            закрытыми разделами ни к чему. */}
          <Route element={<DesktopShell />}>
            {/* Документы — без проверки онбординга: на них ссылается и его
              первый шаг. Гостю каркас показывает сайдбар без разделов. */}
            <Route path={ROUTES.documents} element={<DocumentsPage />} />
            <Route path={`${ROUTES.documents}/:id`} element={<DocumentPage />} />
            {/* Экраны с нижними табами — под общим каркасом MainLayout. Только
              корневые разделы (см. `TABS` в `TabBar`) — как в Instagram, панель
              видна лишь на главных вкладках, а не на любом вложенном экране. */}
            <Route element={<MainLayout />}>
              <Route path={ROUTES.home} element={<HomePage />} />
              <Route path={ROUTES.vocabulary} element={<DictionariesPage />} />
              <Route path={ROUTES.profile} element={<ProfilePage />} />
              <Route path={ROUTES.statistics} element={<StatisticsPage />} />
            </Route>
            {/* Без id — создать разговор, с id — открыть существующий. Чат — не таб,
              открывается поверх основного экрана без нижней панели. */}
            <Route path={ROUTES.chat} element={<ChatPage />} />
            <Route path={`${ROUTES.chat}/:id`} element={<ChatPage />} />
            {/* Итоги разговора — после выхода из чата крестиком. */}
            <Route path={`${ROUTES.chat}/:id/report`} element={<DialogueReportPage />} />
            {/* Превью ролевой игры перед стартом разговора — тоже поверх основного экрана. */}
            <Route path={`${ROUTES.roleplay}/:id`} element={<RoleplayDetailPage />} />
            {/* Детали идиомы (перевод, пример, «Начать диалог») — тоже поверх основного экрана. */}
            <Route path={`${ROUTES.idiom}/:id`} element={<IdiomDetailPage />} />
            {/* Полный список ролевых игр (со статус-фильтром) — вход по кнопке «Показать ещё» из `widgets/topics`. */}
            <Route path={ROUTES.roleplayAll} element={<RoleplayAllPage />} />
            {/* Полный список идиом (со статус-фильтром) — вход по кнопке «Показать ещё» из `widgets/topics`. */}
            <Route path={ROUTES.idiomAll} element={<IdiomAllPage />} />
            {/* Карусель выбора собеседника — общий шаг перед чатом что для свободной практики, что для ролевой игры. */}
            <Route path={ROUTES.tutorSelect} element={<TutorSelectPage />} />
            {/* Повторная проверка уровня с «Аналитики» — поверх, без нижних табов, как чат. */}
            <Route path={ROUTES.levelCheck} element={<LevelCheckPage />} />
            {/* «Учить»/«Повторять» — тоже поверх основного экрана: во время сессии
              карточек переключаться между разделами незачем. */}
            <Route path={ROUTES.vocabularyLearn} element={<LearnPage />} />
            <Route path={ROUTES.vocabularyReview} element={<ReviewPage />} />
            <Route path={ROUTES.vocabularyLists} element={<MyListsManagePage />} />
            <Route path={ROUTES.mistakes} element={<MistakesPage />} />
            <Route path={ROUTES.settings} element={<SettingsPage />} />
            <Route path={ROUTES.subscription} element={<SubscriptionPage />} />
            {/* Пейволл с баннера «Премиум» в профиле — поверх, без нижних табов. */}
            <Route path={ROUTES.premium} element={<PremiumPage />} />
            <Route path={`${ROUTES.subscriptionPayment}/:id`} element={<PaymentDetailsPage />} />
            {/* «Персонализация» — подраздел профиля со своей кнопкой «назад», не таб. */}
            <Route path={ROUTES.personalization} element={<PersonalizationPage />} />
            <Route
              path={ROUTES.personalizationInterests}
              element={<PersonalizationInterestsPage />}
            />
            <Route path={ROUTES.personalizationGoal} element={<PersonalizationGoalPage />} />
            {/* «База знаний» — вложена в «Персонализацию», своей кнопкой «назад». */}
            <Route path={ROUTES.memory} element={<MemoryPage />} />
            <Route path={ROUTES.history} element={<HistoryPage />} />
          </Route>
          {import.meta.env.DEV && <Route path={ROUTES.avatarLab} element={<AvatarLabPage />} />}
          {import.meta.env.DEV && <Route path={ROUTES.sttLab} element={<SttLabPage />} />}
          <Route path="*" element={<Navigate to={ROUTES.root} replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
